/**
 * useManufacturingMaterialPurchases — drives the Manufacturing >
 * Material Purchases screen (mirror of useWarehouseProductPurchases).
 *
 *   - Per-material summary table: in_stock_units +
 *     pending_invoice_count + link_active per active raw material.
 *   - Receive modal: opens on a material row, lists eligible Manage
 *     Purchases via supplier+invoice dropdowns. Once a supplier is
 *     picked, the modal shows EVERY raw material linked to that
 *     supplier with its on-hand and a qty input so one invoice can be
 *     distributed across multiple materials. Σ allocations × 1000 ≤
 *     MP.remaining_millis. Atomic batch
 *     POST /manufacturing/material-purchases/allocate.
 */

import { useCallback, useEffect, useState } from 'react'
import { useAuth } from '../context/AuthContext'
import { APIROUTES, fillPath } from '../constants/routes.constants'
import { mutateJson } from '../utils/api/mutateJson'
import { parseApiError } from '../utils/api/parseApiError'

/** One row in the per-material summary table. */
export interface MaterialAggregateDto {
  readonly raw_material_id: string
  readonly raw_material_name: string
  readonly unit_label: string | null
  /** Owning product (raw_materials.product_id) — drives the page's
   *  product-filter dropdown. */
  readonly product_id: string
  readonly product_name: string
  readonly in_stock_units: number
  readonly pending_invoice_count: number
  readonly link_active: boolean
}

/** Material entry in the modal's per-supplier qty-input table. */
export interface MaterialOnHandRef {
  readonly raw_material_id: string
  readonly raw_material_name: string
  readonly unit_label: string | null
  readonly on_hand: number
}

/** Unused invoice for the modal's invoice dropdown (grouped by supplier). */
export interface UnusedInvoiceDto {
  readonly purchase_id: string
  readonly invoice_no: string
  readonly purchased_millis: number    // MP REMAINING capacity in millis
  readonly received_millis: number
}

export interface SupplierWithInvoicesDto {
  readonly supplier_id: string
  readonly supplier_name: string
  readonly invoices: ReadonlyArray<UnusedInvoiceDto>
  /** Materials this supplier covers (with current on-hand). The modal
   *  renders these once an invoice is picked so the operator can
   *  distribute the MP qty across multiple materials in one Accept. */
  readonly materials: ReadonlyArray<MaterialOnHandRef>
}

export interface MaterialReceiveOptionsDto {
  readonly material: {
    readonly id: string
    readonly name: string
    readonly unit_label: string | null
  }
  readonly suppliers: ReadonlyArray<SupplierWithInvoicesDto>
}

export interface ReceiveLine {
  readonly raw_material_id: string
  readonly quantity: number
}

export interface UseManufacturingMaterialPurchasesReturn {
  readonly materialAggregates: MaterialAggregateDto[]
  readonly isAggregatesLoading: boolean
  readonly reloadAggregates: () => void
  readonly getReceiveOptionsByMaterial: (
    materialId: string
  ) => Promise<{ ok: true; data: MaterialReceiveOptionsDto } | { ok: false; message: string }>
  readonly allocate: (
    input: { managePurchaseId: string; lines: ReadonlyArray<ReceiveLine> }
  ) => Promise<{ ok: true; ids: ReadonlyArray<string> } | { ok: false; field: string | null; message: string }>
}

export function useManufacturingMaterialPurchases(): UseManufacturingMaterialPurchasesReturn {
  const { authedFetch } = useAuth()

  const [materialAggregates, setMaterialAggregates] = useState<MaterialAggregateDto[]>([])
  const [isAggregatesLoading, setIsAggregatesLoading] = useState<boolean>(true)
  const [aggregatesTick, setAggregatesTick] = useState<number>(0)
  const reloadAggregates = useCallback((): void => setAggregatesTick((t) => t + 1), [])

  useEffect(() => {
    const ac = new AbortController()
    setIsAggregatesLoading(true)
    ;(async () => {
      try {
        const res = await authedFetch(APIROUTES.MANUFACTURINGMATERIALPURCHASESBYMATERIAL, { signal: ac.signal })
        if (!res.ok) return
        const body = (await res.json()) as { success: true; data: { items: MaterialAggregateDto[] } }
        if (!ac.signal.aborted) setMaterialAggregates(body.data.items)
      } catch {
        /* aborted */
      } finally {
        if (!ac.signal.aborted) setIsAggregatesLoading(false)
      }
    })()
    return () => ac.abort()
  }, [authedFetch, aggregatesTick])

  const getReceiveOptionsByMaterial = useCallback(
    async (materialId: string): Promise<
      | { ok: true; data: MaterialReceiveOptionsDto }
      | { ok: false; message: string }
    > => {
      const url = fillPath(APIROUTES.MANUFACTURINGMATERIALRECEIVEOPTIONS, { materialId })
      const res = await authedFetch(url)
      if (!res.ok) {
        const { message } = await parseApiError(res)
        return { ok: false, message }
      }
      const body = (await res.json()) as { success: true; data: MaterialReceiveOptionsDto }
      return { ok: true, data: body.data }
    },
    [authedFetch]
  )

  const allocate = useCallback(
    async (input: { managePurchaseId: string; lines: ReadonlyArray<ReceiveLine> }) => {
      const r = await mutateJson<{ ids: ReadonlyArray<string> }>(
        authedFetch,
        APIROUTES.MANUFACTURINGMATERIALPURCHASEALLOCATE,
        'POST',
        {
          manage_purchase_id: input.managePurchaseId,
          lines: input.lines
        }
      )
      if (!r.ok) return r
      setAggregatesTick((t) => t + 1)
      return { ok: true as const, ids: r.data.ids }
    },
    [authedFetch]
  )

  return {
    materialAggregates,
    isAggregatesLoading,
    reloadAggregates,
    getReceiveOptionsByMaterial,
    allocate
  }
}
